import { GiftCouponSettlementSummaryItem } from '@shared/api/couponSettlement/couponSettlement.interface';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import { couponMariShare, rebateMariShare } from './mari-share.utils';
import { computeRebatePayableFromLines } from './rebate-table.utils';
import { getSettlementPayoutDate } from './settlement.utils';

// ─────────────────────────────────────────────
// 통합 정산(배송형 + 쿠폰형) 월별 합계
//
// 백엔드 정산 문서는 배송형(gift-rebate-settlement)·쿠폰형(gift-coupon-settlement)
// 으로 분리돼 있고 그대로 유지한다. 여기서는 상조관리자가 "이번 달에 내가 얼마인지"를
// 한 화면에서 보게, 두 종류의 정산 문서를 (연,월) 키로 묶어 금액만 합산한다.
//
// 금액 기준은 각 화면과 동일하게 맞춘다 (표시 금액이 탭마다 달라지면 안 된다):
// - 배송형: Paid는 동결 totalAmount, Draft는 라인 건별 내림 합(computeRebatePayableFromLines)
//   — RebateHistoryYearCalendar와 동일.
// - 쿠폰형: summary API의 totalAmount 그대로 (overrideAmount 동결 반영, 음수 가능)
//   — CouponHistorySection과 동일.
// ─────────────────────────────────────────────

/**
 * 합산에 들어간 정산서 1건 — 상세 모달을 바로 띄우고(탭 이동 없이),
 * 차수마다 다른 지급일을 각각 표기하기 위해 문서 단위 정보를 남긴다.
 */
export interface UnifiedSideDoc {
  id: string;
  /** 분할 지급 차수 (배송형만 2 이상이 될 수 있고, 없으면 1) */
  seq: number;
  status: GiftSettlementStatus;
  amount: number;
  executedAt?: Date;
  /** 상조명 — 월 전체 합계 보기처럼 여러 상조가 한 행에 접힐 때 구분용 */
  brandName?: string;
  /**
   * 마리 수익금(판매가 1%) — 상조 정산금과 별개로 마리에게 추가 지급할 금액.
   * `withMariShare` 옵션일 때만 채워지고, 미대상(가온 직접·고정 리베이트)이면 null.
   * **admin·마리 화면 전용 — 상조 화면에는 노출 금지.**
   */
  mariShare?: number | null;
}

/** 월 행의 한쪽(배송형 또는 쿠폰형) 집계 */
export interface UnifiedMonthlySide {
  /** 그 달 정산금 합계 (문서가 여러 건이면 합산). 음수 가능(쿠폰형 상계금) */
  amount: number;
  /**
   * 표시 상태 — 문서가 여러 건일 때 하나라도 Draft면 Draft(=정산예정).
   * "아직 안 들어온 돈"이 완료로 보이지 않게 보수적으로 잡는다.
   */
  status: GiftSettlementStatus;
  /** 지급(예정)일 — 문서 중 가장 늦은 값 (문서별 값은 {@link docs}) */
  executedAt?: Date;
  /** 정산 문서 수 (보통 1) */
  docCount: number;
  /** 합산에 들어간 정산서들 (차수·지급일 개별 표기 + 상세 모달용) */
  docs: UnifiedSideDoc[];
  /** 마리 수익금 합 (미대상 문서는 0으로 합산). `withMariShare` 옵션일 때만 의미 있음 */
  mariShare: number;
}

/** 월별 통합 행 (byBrand면 상조 단위로 쪼개진다) */
export interface UnifiedMonthlyRow {
  /** `${year}-${month}` (byBrand면 `|상조id` 추가) */
  key: string;
  year: number;
  month: number;
  /** 상조 id — byBrand로 만들었을 때만 */
  brandId?: string;
  /** 상조명 (corpName → nickName → id) */
  brandName?: string;
  /** 마리 경유 여부 */
  viaMari?: boolean;
  /** 배송형 정산 (그 달 문서가 없으면 undefined) */
  delivery?: UnifiedMonthlySide;
  /** 쿠폰형 정산 (그 달 문서가 없으면 undefined) */
  coupon?: UnifiedMonthlySide;
  /** 배송형 + 쿠폰형 */
  totalAmount: number;
  /** 지급완료(Paid)로 확정된 금액 합 */
  paidAmount: number;
  /** 정산예정(Draft) 금액 합 = 아직 받지 못한 금액 */
  draftAmount: number;
  /**
   * 마리 수익금 합 (배송형 + 쿠폰형). 상조 정산금(`totalAmount`)에 **포함되지 않는**
   * 별도 지급액이다. `withMariShare` 옵션일 때만 채워진다(그 외 0).
   */
  mariShare: number;
}

/** 통합 표 전체 합계 */
export interface UnifiedTotals {
  deliveryAmount: number;
  couponAmount: number;
  totalAmount: number;
  paidAmount: number;
  draftAmount: number;
  monthCount: number;
  /** 마리 수익금 합 — 상조 정산금과 별개(추가 지급). admin·마리 화면 전용 */
  mariShare: number;
}

/** 배송형 정산 문서의 표시 금액 — Paid는 동결값, Draft는 라인 건별 내림 합 */
export const rebateDisplayAmount = (s: GiftRebateSettlement): number =>
  s.status === GiftSettlementStatus.Paid
    ? s.totalAmount
    : computeRebatePayableFromLines(s.lines);

/** (연,월) 정렬 키 — 내림차순 정렬에 사용 */
const ymKey = (year: number, month: number) => year * 12 + month;

/** 같은 달 문서를 한 side로 누적한다 */
const accumulate = (
  side: UnifiedMonthlySide | undefined,
  amount: number,
  status: GiftSettlementStatus,
  executedAt: Date | string | undefined,
  doc: { id: string; seq?: number; brandName?: string; mariShare?: number | null },
): UnifiedMonthlySide => {
  const at = executedAt ? new Date(executedAt) : undefined;
  const validAt = at && !Number.isNaN(at.getTime()) ? at : undefined;
  const entry: UnifiedSideDoc = {
    id: doc.id,
    seq: doc.seq ?? 1,
    status,
    amount,
    executedAt: validAt,
    brandName: doc.brandName,
    mariShare: doc.mariShare,
  };
  if (!side) {
    return {
      amount,
      status,
      executedAt: validAt,
      docCount: 1,
      docs: [entry],
      mariShare: doc.mariShare ?? 0,
    };
  }
  return {
    amount: side.amount + amount,
    mariShare: side.mariShare + (doc.mariShare ?? 0),
    // 하나라도 미지급이면 미지급으로 본다
    status:
      side.status === GiftSettlementStatus.Draft || status === GiftSettlementStatus.Draft
        ? GiftSettlementStatus.Draft
        : GiftSettlementStatus.Paid,
    executedAt:
      validAt && (!side.executedAt || validAt > side.executedAt)
        ? validAt
        : side.executedAt,
    docCount: side.docCount + 1,
    docs: [...side.docs, entry].sort((a, b) => a.seq - b.seq),
  };
};

/** 배송형 정산 문서에서 상조(bugoBrand) 표시 정보를 뽑는다 (populate 안 됐으면 id만) */
export const rebateBrandMeta = (
  s: GiftRebateSettlement,
): { brandId?: string; brandName?: string; viaMari?: boolean } => {
  const bb = s.bugoBrand;
  if (typeof bb === 'string') return { brandId: bb };
  if (!bb) return {};
  return {
    brandId: bb._id,
    brandName: bb.bugoAgency?.corpName || bb.bugoAgency?.nickName || bb._id,
    viaMari: bb.giftAccountConfig?.viaMari,
  };
};

/** {@link buildUnifiedMonthlyRows} 옵션 */
export interface BuildUnifiedOptions {
  /**
   * true면 (연,월,상조) 단위로 행을 나눈다 — 상조가 여럿인 관리자·마리 화면용.
   * false(기본)면 (연,월)로만 묶는다 — 본인 상조만 보는 상조관리자 화면용.
   */
  byBrand?: boolean;
  /**
   * 배송형 표시 금액 계산식 주입 — 화면 계열마다 기준이 달라(상조 화면은 라인 재계산,
   * 관리자 화면은 저장된 totalAmount) 같은 탭에서 보는 금액과 어긋나지 않게 맞춘다.
   * 기본값은 상조 화면 기준({@link rebateDisplayAmount}).
   */
  rebateAmount?: (s: GiftRebateSettlement) => number;
  /**
   * 마리 수익금(판매가 1%) 컬럼 계산 — **admin·마리 화면에서만 true**.
   * 상조 화면(AgencyGiftSettlement*)은 절대 켜지 않는다(존재 자체를 노출 금지).
   */
  withMariShare?: boolean;
}

/**
 * 배송형·쿠폰형 정산 문서를 (연,월)[,상조]로 묶어 통합 행을 만든다.
 * 정렬은 최신 월 먼저(연·월 내림차순), 같은 달 안에서는 상조명 오름차순.
 */
export const buildUnifiedMonthlyRows = (
  rebates: GiftRebateSettlement[] | undefined,
  coupons: GiftCouponSettlementSummaryItem[] | undefined,
  options: BuildUnifiedOptions = {},
): UnifiedMonthlyRow[] => {
  const {
    byBrand = false,
    rebateAmount = rebateDisplayAmount,
    withMariShare = false,
  } = options;
  const map = new Map<string, UnifiedMonthlyRow>();

  const rowOf = (
    year: number,
    month: number,
    meta: { brandId?: string; brandName?: string; viaMari?: boolean },
  ): UnifiedMonthlyRow => {
    const key = byBrand
      ? `${year}-${month}|${meta.brandId ?? '__none__'}`
      : `${year}-${month}`;
    const existing = map.get(key);
    if (existing) {
      // 브랜드명은 먼저 들어온 쪽이 비어 있을 수 있어(populate 누락) 채워준다
      if (byBrand && !existing.brandName && meta.brandName) {
        existing.brandName = meta.brandName;
      }
      if (byBrand && existing.viaMari === undefined && meta.viaMari !== undefined) {
        existing.viaMari = meta.viaMari;
      }
      return existing;
    }
    const created: UnifiedMonthlyRow = {
      key,
      year,
      month,
      totalAmount: 0,
      paidAmount: 0,
      draftAmount: 0,
      mariShare: 0,
      ...(byBrand ? meta : {}),
    };
    map.set(key, created);
    return created;
  };

  (rebates ?? []).forEach((s) => {
    const meta = rebateBrandMeta(s);
    const row = rowOf(s.year, s.month, meta);
    row.delivery = accumulate(row.delivery, rebateAmount(s), s.status, s.executedAt, {
      id: s._id,
      seq: s.seq,
      brandName: meta.brandName,
      mariShare: withMariShare ? rebateMariShare(s) : undefined,
    });
  });

  (coupons ?? []).forEach((s) => {
    const row = rowOf(s.year, s.month, {
      brandId: s.bugoBrandId,
      brandName: s.bugoBrandName,
      viaMari: s.viaMari,
    });
    // 쿠폰형은 차수 개념이 없어 항상 1차
    row.coupon = accumulate(row.coupon, s.totalAmount, s.status, s.executedAt, {
      id: s._id,
      brandName: s.bugoBrandName,
      mariShare: withMariShare ? couponMariShare(s) : undefined,
    });
  });

  return Array.from(map.values())
    .map((row) => {
      const sides = [row.delivery, row.coupon].filter(Boolean) as UnifiedMonthlySide[];
      row.totalAmount = sides.reduce((sum, s) => sum + s.amount, 0);
      row.paidAmount = sides
        .filter((s) => s.status === GiftSettlementStatus.Paid)
        .reduce((sum, s) => sum + s.amount, 0);
      row.draftAmount = sides
        .filter((s) => s.status === GiftSettlementStatus.Draft)
        .reduce((sum, s) => sum + s.amount, 0);
      row.mariShare = sides.reduce((sum, s) => sum + s.mariShare, 0);
      return row;
    })
    .sort(
      (a, b) =>
        ymKey(b.year, b.month) - ymKey(a.year, a.month) ||
        (a.brandName ?? '').localeCompare(b.brandName ?? ''),
    );
};

/** 표시 중인 행들의 합계 */
export const computeUnifiedTotals = (rows: UnifiedMonthlyRow[]): UnifiedTotals => ({
  deliveryAmount: rows.reduce((sum, r) => sum + (r.delivery?.amount ?? 0), 0),
  couponAmount: rows.reduce((sum, r) => sum + (r.coupon?.amount ?? 0), 0),
  totalAmount: rows.reduce((sum, r) => sum + r.totalAmount, 0),
  paidAmount: rows.reduce((sum, r) => sum + r.paidAmount, 0),
  draftAmount: rows.reduce((sum, r) => sum + r.draftAmount, 0),
  monthCount: rows.length,
  mariShare: rows.reduce((sum, r) => sum + r.mariShare, 0),
});

/** 두 side를 하나로 합친다 (월 전체 합계 보기 — 상조 구분 없이 접을 때) */
const mergeSide = (
  a?: UnifiedMonthlySide,
  b?: UnifiedMonthlySide,
): UnifiedMonthlySide | undefined => {
  if (!a) return b;
  if (!b) return a;
  return {
    amount: a.amount + b.amount,
    status:
      a.status === GiftSettlementStatus.Draft || b.status === GiftSettlementStatus.Draft
        ? GiftSettlementStatus.Draft
        : GiftSettlementStatus.Paid,
    executedAt:
      a.executedAt && b.executedAt
        ? a.executedAt > b.executedAt
          ? a.executedAt
          : b.executedAt
        : a.executedAt ?? b.executedAt,
    docCount: a.docCount + b.docCount,
    docs: [...a.docs, ...b.docs],
    mariShare: a.mariShare + b.mariShare,
  };
};

/**
 * 상조별 행을 (연,월) 단위로 접는다 — 필터가 적용된 행 그대로 월 합계를 보고 싶을 때.
 * (원본 문서를 다시 집계하지 않으므로 화면 필터 결과와 항상 일치한다.)
 */
export const collapseUnifiedRowsToMonth = (
  rows: UnifiedMonthlyRow[],
): UnifiedMonthlyRow[] => {
  const map = new Map<string, UnifiedMonthlyRow>();
  rows.forEach((r) => {
    const key = `${r.year}-${r.month}`;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, {
        ...r,
        key,
        brandId: undefined,
        brandName: undefined,
        viaMari: undefined,
      });
      return;
    }
    prev.delivery = mergeSide(prev.delivery, r.delivery);
    prev.coupon = mergeSide(prev.coupon, r.coupon);
    prev.totalAmount += r.totalAmount;
    prev.paidAmount += r.paidAmount;
    prev.draftAmount += r.draftAmount;
    prev.mariShare += r.mariShare;
  });
  return Array.from(map.values());
};

/** 상조별 행을 정산월 그룹으로 묶는다 (월 소계 렌더용, 정렬 순서 유지) */
export const groupUnifiedRowsByMonth = (
  rows: UnifiedMonthlyRow[],
): {
  year: number;
  month: number;
  rows: UnifiedMonthlyRow[];
  totals: UnifiedTotals;
}[] => {
  const groups: { year: number; month: number; rows: UnifiedMonthlyRow[] }[] = [];
  rows.forEach((r) => {
    const last = groups[groups.length - 1];
    if (last && last.year === r.year && last.month === r.month) last.rows.push(r);
    else groups.push({ year: r.year, month: r.month, rows: [r] });
  });
  return groups.map((g) => ({ ...g, totals: computeUnifiedTotals(g.rows) }));
};

// ── 지급일 기준 집계 ────────────────────────────────────────────
// "특정 날짜에 총 얼마가 나갔나(나갈 예정인가)"를 보는 축. 정산월 축과 직교한다
// (6월 1차와 5월분이 같은 날 함께 이체될 수 있고, 6월 1·2차는 다른 날 나간다).

/** 지급일 하나에 묶이는 정산서들 */
export interface PayoutDateGroup {
  /** `${YYYY-MM-DD}|paid|planned` */
  key: string;
  date: Date;
  /** true=실제 지급 완료일, false=지급 예정일 */
  paid: boolean;
  deliveryAmount: number;
  couponAmount: number;
  totalAmount: number;
  docCount: number;
  /** 구성 내역 (정산월·차수[·상조]) */
  items: { key: string; label: string; amount: number }[];
  /** 그 날 함께 나가는 마리 수익금 합 (admin·마리 화면에서만 표기) */
  mariShare: number;
}

const ymd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

/**
 * 표시 중인 행들을 **지급(예정)일** 기준으로 다시 묶는다.
 * 지급일이 없는 문서(미지급)는 정산월 기준 예정일(다음달 15일)로 잡는다 —
 * 화면 금액 셀의 날짜와 같은 규칙이라 두 표의 날짜가 항상 일치한다.
 */
export const buildPayoutDateGroups = (
  rows: UnifiedMonthlyRow[],
  options: { showBrand?: boolean } = {},
): PayoutDateGroup[] => {
  const { showBrand = false } = options;
  const map = new Map<string, PayoutDateGroup>();

  rows.forEach((r) => {
    (
      [
        ['delivery', r.delivery],
        ['coupon', r.coupon],
      ] as const
    ).forEach(([kind, side]) => {
      if (!side) return;
      side.docs.forEach((d) => {
        const paid = d.status === GiftSettlementStatus.Paid;
        const date = d.executedAt ?? getSettlementPayoutDate(r.year, r.month);
        const key = `${ymd(date)}|${paid ? 'paid' : 'planned'}`;
        let g = map.get(key);
        if (!g) {
          g = {
            key,
            date,
            paid,
            deliveryAmount: 0,
            couponAmount: 0,
            totalAmount: 0,
            docCount: 0,
            items: [],
            mariShare: 0,
          };
          map.set(key, g);
        }
        if (kind === 'delivery') g.deliveryAmount += d.amount;
        else g.couponAmount += d.amount;
        g.totalAmount += d.amount;
        g.mariShare += d.mariShare ?? 0;
        g.docCount += 1;
        g.items.push({
          key: d.id,
          label:
            `${r.month}월${d.seq > 1 ? ` ${d.seq}차` : ''}` +
            (showBrand && d.brandName ? ` ${d.brandName}` : ''),
          amount: d.amount,
        });
      });
    });
  });

  return Array.from(map.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
};

/** 행에 등장하는 상조 목록 (이름 오름차순, 필터 칩·셀렉트용) */
export const unifiedBrandOptions = (
  rows: UnifiedMonthlyRow[],
): { id: string; name: string }[] => {
  const map = new Map<string, string>();
  rows.forEach((r) => {
    if (r.brandId) map.set(r.brandId, r.brandName || r.brandId);
  });
  return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

/** 행에 등장하는 연도 목록 (내림차순) */
export const unifiedYearOptions = (rows: UnifiedMonthlyRow[]): number[] =>
  Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => b - a);
